<template>
  <section class="records-page">
    <header class="nav">
      <button type="button" class="back" @click="$router.back()" :aria-label="$t('back')">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
      </button>
      <h1>{{ $t('records') }}</h1>
    </header>

    <div class="tabs">
      <button
        v-for="(tab, idx) in tabs"
        :key="tab.key"
        type="button"
        :class="{ active: active === idx }"
        @click="switchTab(idx)"
      >
        {{ tabTitle(tab.key) }}
      </button>
    </div>

    <div class="body">
      <div class="toolbar">
        <strong class="cur-title">{{ tabTitle(currentTab.key) }}</strong>
        <div class="actions">
          <button type="button" class="tool-btn" :disabled="currentState.loading" @click="reloadCurrent">
            {{ currentState.loading ? $t('refreshing') : $t('refresh') }}
          </button>
          <button v-if="currentTab.key === 'activity'" type="button" class="tool-btn primary" @click="claimBonus">
            {{ $t('claim') }}
          </button>
        </div>
      </div>

      <div v-if="currentState.loading && currentState.items.length === 0" class="empty">{{ $t('loading') }}</div>

      <div v-else-if="currentState.items.length === 0" class="empty">
        <img src="/recovered-assets/no_data.svg" alt="no-data" />
        <p>{{ $t('noRecords') }}</p>
      </div>

      <ul v-else class="record-list">
        <li
          v-for="(row, idx) in currentState.items"
          :key="row.id || `${currentTab.key}_${idx}`"
          class="record-item"
        >
          <template v-if="currentTab.key === 'withdraw'">
            <div class="line"><span>{{ $t('amount') }}</span><strong>{{ numFormat(rowAmount(row), 2) }} USDT</strong></div>
            <div class="line"><span>{{ $t('status') }}</span><span :class="statusClass(row.status)">{{ withdrawStatus(row.status) }}</span></div>
            <div v-if="row.remarks" class="line"><span>{{ $t('note') }}</span><span>{{ row.remarks }}</span></div>
            <div class="line"><span>{{ $t('time') }}</span><span>{{ formatDate(rowTime(row)) }}</span></div>
          </template>

          <template v-else>
            <div class="line"><span>{{ $t('amount') }}</span><strong>{{ numFormat(row.money, row.coin === 'ETH' ? 6 : 2) }} {{ row.coin || 'USDT' }}</strong></div>
            <div class="line"><span>{{ $t('balance') }}</span><span>{{ numFormat(row.after, row.coin === 'ETH' ? 6 : 2) }} {{ row.coin || 'USDT' }}</span></div>
            <div v-if="row.memo" class="line"><span>{{ $t('note') }}</span><span>{{ row.memo }}</span></div>
            <div class="line"><span>{{ $t('time') }}</span><span>{{ formatDate(row.createtime) }}</span></div>
          </template>
        </li>
      </ul>

      <button
        v-if="canLoadMoreCurrent"
        type="button"
        class="more"
        :disabled="currentState.loading"
        @click="loadCurrent(false)"
      >
        {{ currentState.loading ? $t('loading') : $t('loadMore') }}
      </button>
    </div>
  </section>
</template>

<script>
import { getUserInfo } from "../recovered/sign-api";
import {
  getOrderExchange,
  getOrderWithdraw,
  getOrderProfit,
  getActivityRecords,
  claimActivityBonus,
} from "../recovered/records-api";

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function createState() {
  return {
    items: [],
    page: 1,
    limit: 10,
    total: 0,
    loading: false,
  };
}

export default {
  name: "RecordsView",
  i18n: {
    zh: {
      records: "记录",
      back: "返回",
      exchange: "兑换",
      withdraw: "提现",
      profit: "收益",
      activity: "活动",
      refresh: "刷新",
      refreshing: "刷新中",
      claim: "领取",
      loading: "加载中…",
      loadMore: "加载更多",
      noRecords: "暂无记录",
      amount: "金额",
      status: "状态",
      time: "时间",
      balance: "余额",
      note: "说明",
      submitted: "已提交",
      approved: "通过",
      rejected: "拒绝",
    },
    en: {
      records: "Records",
      back: "Back",
      exchange: "Exchange",
      withdraw: "Withdraw",
      profit: "Profit",
      activity: "Activity",
      refresh: "Refresh",
      refreshing: "Refreshing",
      claim: "Claim",
      loading: "Loading…",
      loadMore: "Load More",
      noRecords: "No records",
      amount: "Amount",
      status: "Status",
      time: "Time",
      balance: "Balance",
      note: "Note",
      submitted: "Submitted",
      approved: "Approved",
      rejected: "Rejected",
    },
  },
  data() {
    return {
      active: 0,
      coin: "USDC",
      tabs: [
        { key: "exchange", loader: getOrderExchange },
        { key: "withdraw", loader: getOrderWithdraw },
        { key: "profit", loader: getOrderProfit },
        { key: "activity", loader: getActivityRecords },
      ],
      records: {
        exchange: createState(),
        withdraw: createState(),
        profit: createState(),
        activity: createState(),
      },
    };
  },
  computed: {
    currentTab() {
      return this.tabs[this.active] || this.tabs[0];
    },
    currentState() {
      return this.records[this.currentTab.key];
    },
    canLoadMoreCurrent() {
      const state = this.currentState;
      if (!state || state.loading) return false;
      if (!state.total) return false;
      return state.items.length < state.total;
    },
  },
  mounted() {
    const q = Number(this.$route.query.type);
    if (Number.isFinite(q) && q >= 0 && q < this.tabs.length) {
      this.active = q;
    }
    this.bootstrap();
  },
  methods: {
    tabTitle(key) {
      return this.$t(key);
    },
    numFormat(v, decimals = 2) {
      return toNum(v).toFixed(decimals);
    },
    rowAmount(row) {
      // 兼容多种字段名：money / amount / bpprice(提现) / num
      if (!row) return 0;
      if (row.money !== undefined && row.money !== null) return row.money;
      if (row.amount !== undefined && row.amount !== null) return row.amount;
      if (row.bpprice !== undefined && row.bpprice !== null) return row.bpprice;
      return row.num;
    },
    rowTime(row) {
      if (!row) return 0;
      return row.createtime || row.createTime || row.create_time || row.time;
    },
    formatDate(input) {
      if (!input) return "--";
      // 后端已返回格式化字符串（如 "2026-07-27"），直接展示
      if (typeof input === "string" && !/^\d+$/.test(input.trim())) {
        return input;
      }
      let ts = Number(input);
      if (ts < 1e12) ts *= 1000; // Unix 秒 → 毫秒
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return "--";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const h = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      const s = String(d.getSeconds()).padStart(2, "0");
      return `${y}-${m}-${day} ${h}:${mm}:${s}`;
    },
    withdrawStatus(status) {
      const n = Number(status);
      if (n === 0) return this.$t('submitted');
      if (n === 1) return this.$t('approved');
      if (n === 2) return this.$t('rejected');
      return status === undefined || status === null ? "--" : String(status);
    },
    statusClass(status) {
      const n = Number(status);
      if (n === 1) return "st-ok";
      if (n === 2) return "st-fail";
      return "st-pending";
    },
    async bootstrap() {
      try {
        const info = await getUserInfo();
        const addr = (info && info.address) || "";
        this.coin = addr.startsWith("T") ? "TRX" : "USDC";
      } catch (err) {
        // 忽略，coin 用默认值
      }

      await this.loadCurrent(true);
    },
    switchTab(index) {
      this.active = index;
      this.ensureLoaded(this.currentTab.key);
    },
    ensureLoaded(key) {
      const state = this.records[key];
      if (!state || state.loading) return;
      if (state.items.length === 0) {
        this.loadByKey(key, true);
      }
    },
    reloadCurrent() {
      this.loadCurrent(true);
    },
    loadCurrent(reset) {
      return this.loadByKey(this.currentTab.key, reset);
    },
    async loadByKey(key, reset) {
      const tab = this.tabs.find((x) => x.key === key);
      const state = this.records[key];
      if (!tab || !state || state.loading) return;

      state.loading = true;
      if (reset) {
        state.page = 1;
        state.total = 0;
        state.items = [];
      }

      try {
        const resp = await tab.loader(state.page, state.limit);
        // 兼容返回：
        //  - 提现 withdrawList：data 为数组直出
        //  - 资金流水 records：data 为 { lists, total_count }
        const list = Array.isArray(resp)
          ? resp
          : Array.isArray(resp && resp.lists)
            ? resp.lists
            : Array.isArray(resp && resp.list)
              ? resp.list
              : Array.isArray(resp && resp.data)
                ? resp.data
                : [];
        state.total = toNum(resp && (resp.total_count || resp.total)) || list.length;
        state.items = reset ? list : state.items.concat(list);
        if (list.length >= state.limit) state.page += 1;
      } catch (err) {
        // 接口未就绪时优雅降级为空态，不打断页面
        if (reset) state.items = [];
      } finally {
        state.loading = false;
      }
    },
    async claimBonus() {
      try {
        await claimActivityBonus();
        await this.loadByKey("activity", true);
      } catch (err) {
        // ignore
      }
    },
  },
};
</script>

<style scoped>
.records-page {
  min-height: 100vh;
  max-width: 430px;
  margin: 0 auto;
  background: transparent;
  padding-bottom: 24px;
}

.nav {
  position: relative;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border-bottom: 1px solid var(--pool-line);
}

.nav .back {
  position: absolute;
  left: 6px;
  top: 0;
  bottom: 0;
  width: 44px;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav .back svg {
  width: 22px;
  height: 22px;
  stroke: var(--pool-text);
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.nav h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--pool-text);
}

.tabs {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  background: transparent;
  border-bottom: 1px solid var(--pool-line);
}

.tabs button {
  position: relative;
  height: 46px;
  border: 0;
  background: transparent;
  font-size: 15px;
  color: var(--pool-muted);
}

.tabs button.active {
  color: var(--pool-text);
  font-weight: 600;
}

.tabs button.active::after {
  content: "";
  position: absolute;
  left: 28%;
  right: 28%;
  bottom: 0;
  height: 3px;
  border-radius: 3px;
  background: var(--pool-grad);
}

.body {
  padding: 12px;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.cur-title {
  font-size: 16px;
  color: var(--pool-text);
}

.actions {
  display: flex;
  gap: 8px;
}

.tool-btn {
  border: 1px solid var(--pool-line);
  background: var(--pool-glass-2);
  color: var(--pool-text);
  border-radius: 16px;
  padding: 5px 14px;
  font-size: 13px;
}

.tool-btn:disabled {
  opacity: 0.6;
}

.tool-btn.primary {
  border: 0;
  background: var(--pool-grad);
  color: #04121a;
  font-weight: 700;
}

.empty {
  text-align: center;
  color: var(--pool-muted);
  padding: 60px 0;
  font-size: 14px;
}

.empty img {
  width: 128px;
  height: auto;
  opacity: 0.95;
}

.empty p {
  margin: 12px 0 0;
}

.record-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.record-item {
  position: relative;
  overflow: hidden;
  background: var(--pool-glass);
  border: 1px solid var(--pool-line);
  border-radius: var(--pool-r);
  padding: 14px;
  box-shadow: 0 20px 40px -34px rgba(0, 0, 0, 0.9);
}

.record-item::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.14), transparent 40%);
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

.record-item + .record-item {
  margin-top: 10px;
}

.line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.line + .line {
  margin-top: 8px;
}

.line > span:first-child {
  color: var(--pool-muted);
}

.line strong {
  color: var(--pool-text);
  font-weight: 600;
}

.st-ok {
  color: var(--pool-good);
}

.st-fail {
  color: var(--pool-bad);
}

.st-pending {
  color: var(--pool-gold);
}

.more {
  display: block;
  width: 100%;
  margin-top: 14px;
  border: 1px solid var(--pool-line);
  background: var(--pool-glass-2);
  color: var(--pool-text);
  border-radius: 22px;
  padding: 11px 0;
  font-size: 14px;
}

.more:disabled {
  opacity: 0.6;
}
</style>
